import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { usePlaybooksQuery } from './usePlaybooksQuery';
import { usePlaybooksSearch } from './usePlaybooksSearch';
import { usePlaybooksTableColumns } from './usePlaybooksTableColumns';
import { Playbook } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on Playbook (types.ts). The guidance prefers an
// immutable, non-null key, and there is no better one here: `id` is the only
// non-null field on the type, and ordering playbooks by an opaque id means
// nothing to an operator.
//
// `created` is still the right key. It is immutable — `updated` would reorder
// the list under someone reading it — and listPlaybooks already returns
// created-descending, so the first paint AGREES with the server instead of
// reshuffling what it just returned. Rows missing it sort last, which is
// correct: a missing timestamp is a state, not a small value.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Playbook> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const usePlaybooks = (filters: Record<string, string> = {}) => {
    const query = usePlaybooksQuery(filters);
    const { keyword } = usePlaybooksSearch();
    const { columns } = usePlaybooksTableColumns();

    // Type-guarded flatten: a page that errored is an object without `items`,
    // and a bare flatMap over it would put `undefined` into the record list.
    const items: Playbook[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen. That is a property of the model, not an
    // oversight — it is why the footer states a LOADED count rather than a
    // total, and why `filterTerm` is reported alongside it.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.name?.toLowerCase().includes(needle) ||
        item.displayName?.toLowerCase().includes(needle) ||
        item.description?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Playbook>(matched, DEFAULT_SORT_STATUS);

    // §9: never call fetchNextPage() without checking hasNextPage first —
    // the table's onScrollToBottom fires on every bounce at the end of the list.
    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
